/**
 * @file     HubActionButton.tsx
 * @purpose  The one action button the hub kit panels share (EmptyState,
 *           ResultFigure, SystemPage, GatedPanel): primary filled or outlined,
 *           44px from a phone up, radius 16. A link when it has an href, a
 *           button when it has a handler. No directive, so a Server Component
 *           can render it with an href.
 */
import Button from '@mui/material/Button';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { hubColors } from './hubKitStyles';

export interface HubAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export function HubActionButton({
  action,
  variant = 'primary',
  fullWidth = false,
}: {
  action: HubAction;
  variant?: 'primary' | 'secondary';
  fullWidth?: boolean;
}) {
  const primary = variant === 'primary';
  return (
    <Button
      variant={primary ? 'contained' : 'outlined'}
      href={action.href}
      onClick={action.onClick}
      fullWidth={fullWidth}
      disableElevation
      sx={{
        minHeight: 44,
        px: `${layoutTokens.button.md.paddingX}px`,
        borderRadius: `${layoutTokens.radius.md}px`,
        fontSize: 15,
        fontWeight: fontWeightTokens.medium,
        whiteSpace: 'nowrap',
        ...(primary
          ? {
              backgroundColor: hubColors.interactivePrimary,
              color: hubColors.textOnPrimary,
              '@media (hover: hover)': { '&:hover': { backgroundColor: hubColors.primaryHover } },
            }
          : {
              borderColor: hubColors.borderStrong,
              color: hubColors.textPrimary,
              backgroundColor: 'transparent',
              '@media (hover: hover)': {
                '&:hover': { borderColor: hubColors.textSecondary, backgroundColor: hubColors.interactiveHover },
              },
            }),
      }}
    >
      {action.label}
    </Button>
  );
}
